import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ChangesDialogComponent } from './changes-dialog/changes-dialog.component';
import { Matrix4GProps } from './Matrix4GProps';
import { MatrixProps } from './MatrixProps';
import { Matrix2Props } from './Matrix2Props';
import { Matrix2PlusProps } from './Matrix2PlusProps';
import {
    Camera,
    CameraSettingGroup,
    CameraSettingTab,
    CameraType,
    CameraTypes,
    ExternalCameraSetting,
    GenericCameraSettings,
    GenericCameraSettingsFunctions,
    Group,
    InputType,
    CameraSettingsErrorStateMatcher,
    RenderType,
    TabWithIcon,
    ApiMode,
    cameraSettingGroups,
    tabs,
    cameraList,
    Organization,
    Role
} from './models';
import { ActivatedRoute } from '@angular/router';
import { CameraSettingService } from '@services/camerasettings.service';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { SelectionDialogComponent } from './selection-dialog/selection-dialog.component';
import { faChevronUp, faChevronDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { OrganizationService } from '@services/organization.service';
import { RoleService } from '@services/role.service';

@Component({
    selector: 'app-camera-setting',
    templateUrl: './camera-setting.component.html',
    styleUrls: ['./camera-setting.component.scss']
})
export class CameraSettingComponent implements OnInit, AfterViewInit {
    @ViewChild('scrollableDiv') scrollableDiv: ElementRef;
    @ViewChild('scrollToTopButton') scrollToTopButton: ElementRef;

    selectedCamera: Camera = cameraList[0];
    selectedCameraId: number = cameraList[0].id;
    selectedCameraIdOld: number = 0;
    tabs: TabWithIcon[] = tabs;
    groups: Group[] = cameraSettingGroups;
    selectedTabId: number = 0;
    selectedTabBackup: number = 0;
    searchText: string = '';
    cameraList: Camera[] = cameraList;
    form: FormGroup = null;
    allowPageRender: Boolean = false;
    OrganizationName: any;
    RoleName: any;
    Organizations: Organization[];
    Roles: Role[];
    public get RenderType(): typeof RenderType {
        return RenderType;
    }
    public get InputType(): typeof InputType {
        return InputType;
    }
    matcher = new CameraSettingsErrorStateMatcher();
    FormProperties: GenericCameraSettings[] = []; // Can be unreliable as it is being used to show/hide the properties based on the filter. Whenever FormProperty change we update the initial values
    InitialValues: GenericCameraSettings[] = []; // This contains the actual values and is being used to submit the form

    CameraSettings: CameraSettingTab[];
    panelOpenState: boolean = false;

    mode: ApiMode = ApiMode.User;
    faChevronUp = faChevronUp;
    faChevronDown = faChevronDown;
    faArrowUp = faArrowUp;

    queryCameraId = 0;
    queryUserId = 0;
    queryUsername = '';
    queryRoleId = 0;
    queryRoleName = '';
    queryCompanyId = 0;
    queryCompanyName = '';

    validationError: string =
        '<p>Please Fix the Validation errors before submiting.</p>';

    constructor(
        public dialog: MatDialog,
        public fb: FormBuilder,
        private route: ActivatedRoute,
        public cameraSettingService: CameraSettingService,
        public ChangeDetecter: ChangeDetectorRef,
        private scrollToService: ScrollToService,
        private organizationsService: OrganizationService,
        private roleService: RoleService,
    ) {
        this.ReadQueryString();
        this.SetupCameraSettings();

        if (!this.queryCameraId) {
            this.queryCameraId = 1;
        }
        if (!this.queryUserId && !this.queryRoleId && !this.queryCompanyId) {
            const dialogRef = this.dialog.open(SelectionDialogComponent, {
                disableClose: true,
                data: {
                    properties: {},
                    userId: this.queryUserId,
                    cameraId: this.queryCameraId,
                    userName: this.queryUsername
                }
                // panelClass: 'custom-dialog-container'
            });
            dialogRef.afterClosed().subscribe((result) => {
                if (result && result.Status === true) {
                    console.log(result);
                    this.queryRoleId = result.RoleId;
                    this.queryCompanyId = result.OrganizationId;
                    this.OrganizationName = result.OrganizationName;
                    this.RoleName = result.RoleName;
                    this.mode =
                        result.UpdateType === 'Organization'
                            ? ApiMode.Company
                            : ApiMode.Role;
                    this.GetCameraSettings();
                }
            });
        } else {
            this.GetCameraSettings();
        }
    }
    ngOnInit() {
        this.organizationsService.getOrganizations().subscribe((res: Organization[]) => {
            this.Organizations = res;
            this.OrganizationName = res.find(x => x.CompanyId == this.queryCompanyId)?.Name;
        });
        this.roleService.getRoles().subscribe((res: Role[]) => {
            this.Roles = res;
            this.RoleName = res.find(x => x.ID == this.queryRoleId)?.RoleName;
        });
    }
    ngOnChanges() {
        this.filterInput();
    }
    ngAfterViewInit() {
        this.selectedTabId = 1;
        this.onScroll(null);
    }

    onScroll(e: any) {
        if (this.scrollableDiv.nativeElement.scrollTop > 200) {
            this.scrollToTopButton.nativeElement.style.display = 'block';
        } else {
            this.scrollToTopButton.nativeElement.style.display = 'none';
        }
        console.log("scrolling");
    }

    scrollToTop() {
        this.scrollableDiv.nativeElement.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    SetupCameraSettings() {
        this.CameraSettings = [];
        for (let i in this.tabs) {
            let groups = this.groups.filter((x) => x.tabId == this.tabs[i].id);
            let groupForCameraSetting: CameraSettingGroup[] = groups.map(
                (x: Group) => {
                    return { group: x, isOpen: true };
                }
            );
            groupForCameraSetting.forEach((x) => {
                if (this.selectedCameraId == 1)
                    x.initialValues = MatrixProps.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });
                if (this.selectedCameraId == 2)
                    x.initialValues = Matrix2Props.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });
                if (this.selectedCameraId == 3)
                    x.initialValues = Matrix2PlusProps.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });
                if (this.selectedCameraId == 4)
                    x.initialValues = Matrix4GProps.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });
            });

            this.CameraSettings.push({
                tab: this.tabs[i],
                groups: groupForCameraSetting
            });
        }
    }
    private ReadQueryString() {
        this.route.queryParams.subscribe((params) => {
            this.queryCameraId = parseInt(params.cameraId);
            if (!this.queryCameraId) {
                this.queryCameraId = 1;
            }
            if (params.userId && params.cameraId) {
                this.queryUserId = params.userId;
                this.queryUsername = params.userName;
                this.mode = ApiMode.User;
            } else if (params.roleId && params.cameraId) {
                this.queryRoleId = params.roleId;
                this.mode = ApiMode.Role;

            } else if (params.companyId && params.cameraId) {
                this.queryCompanyId = params.companyId;
                this.mode = ApiMode.Company;
            }
        });
    }

    private FormInitialization() {
        this.FormProperties = this.InitialValues;
        this.FormProperties.reverse().forEach((x) => {
            this.ApplyEffects2(x.propertyName);
        });
        this.InitializeForm();
        this.allowPageRender = true;
    }

    private ApplyEffects2(parentPropertyName: string) {

        let prop = this.FormProperties.find(
            (x) => x.propertyName == parentPropertyName
        );
        let childPropsWithEffects = [];

        if (prop && prop.effects) {
            let effects2 = prop.effects.filter(
                (y) => y.givenValues.indexOf(prop.value) >= 0
            );
            effects2.forEach((effect) => {
                let effectedProps = this.FormProperties.filter((x) => effect.propertyNames.indexOf(x.propertyName) >= 0);
                if (effectedProps && effectedProps.length > 0) {
                    effectedProps.forEach((effectedProp) => {
                        console.log(effectedProp);
                        let originalProp = this.InitialValues.find((z) => z.propertyName == effectedProp.propertyName);
                        if (effect.disabled != null && effect.disabled != undefined) {
                            effectedProp.disabled = effect.disabled;
                            console.log(`${parentPropertyName} ${effect.disabled} ${effectedProp.propertyName}`);
                            this.InitializeForm();
                        }
                        else {
                            effectedProp.disabled = originalProp.disabled;
                        }
                        if (originalProp.effects && !originalProp.disabled) {
                            childPropsWithEffects.push(originalProp);
                        }
                    });
                }
            });
            if (childPropsWithEffects.length > 0) {
                childPropsWithEffects.forEach(x => {
                    console.log("loop apply effects for " + x.propertyName);
                    this.ApplyEffects2(x.propertyName);
                });
            }
        }

    }

    private GetCameraSettings() {
        let k = 0;
        if (this.mode === ApiMode.Company) {
            k = this.queryCompanyId;
        } else if (this.mode === ApiMode.Role) {
            k = this.queryRoleId;
        } else if (this.mode === ApiMode.User) {
            k = this.queryUserId;
        }

        this.cameraSettingService
            .getCameraSettings(k, this.mode, this.queryCameraId)
            .subscribe(
                (x: string) => {
                    this.SelectCamera(this.queryCameraId);
                    let a = JSON.parse(x)[0];
                    let result = {};
                    Object.keys(a).map((e) => (result[e] = a[e]));
                    this.InitialValues.forEach((y) => {
                        let val = result[y.propertyName];
                        if (val != null && val != undefined) {
                            if (y.inputType == InputType.Number) {
                                y.value = Number(val || 0);
                                y.defaultValue = (y.value || 0);
                            } else {
                                y.value = val;
                                y.defaultValue = y.value;
                            }
                        }
                    });

                    this.FormInitialization();
                },
                (err) => {
                    this.SweetAlert(
                        'error',
                        'Technical error',
                        'Unable to acccess the API resources'
                    );
                }
            );
    }

    onCameraChange($event: any) {
        let vm = this;
        console.log('previous Camera : ', this.selectedCameraIdOld);
        let camera_id = $event.source.value;
        console.log('Current Camera : ', $event.source.value);
        function ChangeCameraType() {
            console.log('change called');
            vm.SelectCamera(camera_id);
            vm.selectedTabId = vm.tabs[0].id;
            let cameraId = camera_id;
            vm.queryCameraId = cameraId;
            vm.SelectCamera(vm.queryCameraId);
            vm.GetCameraSettings();
        }
        let props: GenericCameraSettings[] = [];
        this.InitialValues.forEach((prop) => {
            AddToProps(prop);
        });
        function AddToProps(prop: GenericCameraSettings) {
            if (prop.value != prop.defaultValue) {
                props.push(prop);
            }
        }
        console.log(props);
        if (props.length > 0) {
            Swal.fire({
                title: 'You are about to change camera type, all unsaved settings will be lost',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#0f1c24',
                confirmButtonText: 'Yes, I am sure!',
                cancelButtonText: 'No, cancel it!',
                cancelButtonColor: '#FFFFFF',
                showClass: {
                    popup: 'animated fadeInDown faster'
                },
                hideClass: {
                    popup: 'animated fadeOutUp faster'
                },
                reverseButtons: true
            }).then((result) => {
                console.log(result);
                props = [];
                if (result.isConfirmed) {
                    console.log('confirmation');
                    ChangeCameraType();
                } else {
                    console.log('reverting back');
                    this.selectedCamera = this.selectedCamera = cameraList.find(
                        (x: Camera) => x.id == this.selectedCameraIdOld
                    );
                    this.selectedCameraId = this.selectedCameraIdOld;
                    console.log(this.selectedCamera);
                    this.ChangeDetecter.detectChanges();
                }
            });
        } else {
            ChangeCameraType();
        }
    }
    compareCategoryObjects(object1: any, object2: any) {
        return object1 && object2 && object1.id == object2.id;
    }

    SelectCamera(id: number) {
        this.selectedCamera = cameraList.filter((x: Camera) => x.id == id)[0];
        this.selectedCameraId = this.selectedCamera.id;
        switch (id) {
            case 1:
                this.InitialValues = MatrixProps;
                this.FormProperties = MatrixProps;
                break;
            case 2:
                this.InitialValues = Matrix2Props;
                this.FormProperties = Matrix2Props;
                break;
            case 3:
                this.InitialValues = Matrix2PlusProps;
                this.FormProperties = Matrix2PlusProps;
                break;
            case 4:
                this.InitialValues = Matrix4GProps;
                this.FormProperties = Matrix4GProps;
                break;
            default:
                this.InitialValues = MatrixProps;
                this.FormProperties = MatrixProps;
                break;
        }
        this.SetupCameraSettings();
    }
    private InitializeForm() {
        this.form = GenericCameraSettingsFunctions.toFormGroup(
            this.FormProperties
        );
    }

    updateValues(ob: any) {
        this.FormProperties.forEach((x) => {
            if (x.propertyName == ob.name) {
                x.value = ob.value;
            }
        });
        this.ApplyEffects2(ob.name);
        // this.ApplyEffects();

        this.ChangeDetecter.detectChanges();
    }

    onTabChange(tab: any) {
        this.scrollToService.scrollTo({
            target: tab.title + '_' + tab.id,
            duration: 500
        });
        this.selectedTabId = tab.id;
        this.searchText = '';
        this.filterInput();
    }

    onSubmit() {
        if (!this.form.valid) {
            let groupArr = [];
            let error =
                '<ul style="text-align:left;font-size: 15px;color: #F27474;">';
            Object.keys(this.form.controls).forEach((key) => {
                let control = this.form.get(key);
                control.markAsTouched();
                control.updateValueAndValidity();
                const controlErrors: ValidationErrors = control.errors;
                let prop = this.InitialValues.find(
                    (x) => x.propertyName == key
                );
                if (controlErrors != null) {
                    groupArr.push(prop.groupId);
                    Object.keys(controlErrors).forEach((keyError) => {
                        error += `<li>${prop.validationErrorMessages[keyError]}</li>`;
                        console.warn(
                            `${key}, ${keyError}${controlErrors[keyError]}`
                        );
                    });
                }
            });
            error += '</ul>';
            const distinctGroupArr = Array.from(new Set(groupArr));
            this.CameraSettings.forEach((x) => {
                x.groups.forEach((y) => {
                    if (distinctGroupArr.indexOf(y.group.id) >= 0) {
                        y.isOpen = true;
                    }
                });
            });
            this.ChangeDetecter.detectChanges();
            this.SweetAlert('error', 'Please fix the validation errors', error);
            return;
        }
        let modifiedProps: GenericCameraSettings[] = [];
        let props: GenericCameraSettings[] = [];

        this.InitialValues.forEach((prop) => {
            if (prop.value != prop.defaultValue) {
                modifiedProps.push(prop);
            }
            props.push(prop);
        });

        let grouping: CameraSettingTab[] = [];
        for (let i in this.tabs) {
            let groups = this.groups.filter((x) => x.tabId == this.tabs[i].id);
            let groupForCameraSetting: CameraSettingGroup[] = groups.map(
                (x: Group) => {
                    return { group: x, isOpen: true };
                }
            );
            groupForCameraSetting.forEach((x) => {
                x.initialValues = modifiedProps.filter(
                    (y) => y.groupId == x.group.id
                );
            });
            groupForCameraSetting = groupForCameraSetting.filter(
                (x) => x.initialValues.length > 0
            );
            if (groupForCameraSetting.length > 0) {
                grouping.push({
                    tab: this.tabs[i],
                    groups: groupForCameraSetting
                });
            }
        }

        let data = {
            properties: grouping,
            userId: this.queryUserId,
            userName: this.queryUsername,
            roleId: Number(this.queryRoleId),
            roleName: this.RoleName,
            companyId: Number(this.queryCompanyId),
            companyName: this.OrganizationName,
            organizations: this.Organizations,
            roles: this.Roles,
            mode: this.mode,
            cameraId: this.queryCameraId
        };
        console.log("data : ", data);
        const dialogRef = this.dialog.open(ChangesDialogComponent, {
            data: data,
            panelClass: 'custom-dialog-container'
        });
        dialogRef.componentInstance.UndoClick.subscribe((result) => {
            this.InitialValues.forEach((x) => {
                if (x.propertyName == result) {
                    x.value = x.defaultValue;
                }
            });
            this.FormProperties.forEach((x) => {
                if (x.propertyName == result) {
                    x.value = x.defaultValue;
                }
            });
            this.InitializeForm();
            props = props.filter((x) => x.propertyName != result);
        });
        dialogRef.afterClosed().subscribe((result) => {
            console.log(result);
            if (result && result.Status === true) {
                let sCamera: CameraType = 1;
                switch (this.selectedCamera.id) {
                    case CameraTypes.Matrix:
                        sCamera = CameraType.Matrix;
                        break;
                    case CameraTypes.Matrix2:
                        sCamera = CameraType.Matrix2;
                        break;
                    case CameraTypes.Matrix2Plus:
                        sCamera = CameraType.Matrix2Plus;
                        break;
                    case CameraTypes.Matrix4G:
                        sCamera = CameraType.Matrix4G;
                        break;
                }

                let p = props.map((x) => {
                    if (x.inputType == InputType.Number && x.renderType == RenderType.Toggle) {
                        x.value = Number(x.value);
                    }
                    if (x.value === false || x.value === true) {
                        x.value = Number(x.value);
                    }
                    return { [x.propertyName]: x.value };
                });
                let o = { root: {} };
                p.forEach((x) => {
                    Object.assign(o.root, x);
                });
                let cameraSetting: ExternalCameraSetting = null;
                console.log("RoleId : ", result.RoleId);
                console.log("CompanyId : ", result.RoleId);
                if (result.UpdateType === 'User') {
                    cameraSetting = {
                        updateMode: ApiMode.User,
                        cameraType: sCamera,
                        updateID: this.queryUserId,
                        updatePayload: JSON.stringify(o)
                    };
                } else if (result.UpdateType === 'Organization') {
                    cameraSetting = {
                        updateMode: ApiMode.Company,
                        cameraType: sCamera,
                        updateID: result.OrganizationId,
                        updatePayload: JSON.stringify(o)
                    };
                } else if (result.UpdateType === 'Role') {
                    cameraSetting = {
                        updateMode: ApiMode.Role,
                        cameraType: sCamera,
                        updateID: result.RoleId,
                        updatePayload: JSON.stringify(o)
                    };
                } else {
                    cameraSetting = {
                        updateMode: ApiMode.Company,
                        cameraType: sCamera,
                        updateID: 0,
                        updatePayload: JSON.stringify(o)
                    }
                }

                console.log("cameraSettings : ", cameraSetting);

                this.cameraSettingService
                    .updateCameraSettings(cameraSetting)
                    .subscribe(
                        (x) => {
                            this.SweetAlert(
                                'success',
                                'Success',
                                'Settings saved successfully'
                            ).then((x) => {
                                this.form.reset();
                                this.GetCameraSettings();
                                this.FormInitialization();

                            });
                        },
                        (err) => {
                            this.SweetAlert(
                                'error',
                                'Technical error',
                                'Unable to save camera settings'
                            );
                        }
                    );
            }
        });
    }

    filterInput() {
        console.log('selected tab : ', this.selectedTabId);
        this.FormProperties = this.InitialValues.filter((property) => {
            if (!this.searchText || this.PropertyNameCheck(property)) {
                return property;
            }
        });
    }

    PropertyNameCheck(property: GenericCameraSettings): boolean {
        let vm = this;
        let a =
            property.displayName
                .toLowerCase()
                .indexOf(vm.searchText.toLowerCase()) > -1;
        return a;
    }
    PropertyNameDisplayCheck(propertyName: string): boolean {
        let vm = this;
        let a =
            propertyName.toLowerCase().indexOf(vm.searchText.toLowerCase()) >
            -1;
        return a || !this.searchText;
    }
    PropertyNameDisplayCheckGroup(
        properties: GenericCameraSettings[]
    ): boolean {
        let vm = this;
        let a: boolean = false;
        properties.forEach((x) => {
            a =
                a ||
                x.displayName
                    .toLowerCase()
                    .indexOf(vm.searchText.toLowerCase()) > -1;
        });
        return a || !this.searchText;
    }
    updateSearchText($event: any) {
        $event.preventDefault();
        this.searchText = $event.target.value;
        this.filterInput();
    }

    private SweetAlert(icon: SweetAlertIcon, title: string, text: string) {
        return Swal.fire({
            title: title,
            html: text,
            icon: icon,
            confirmButtonColor: '#0f1c24',
            showClass: {
                popup: 'animated fadeInDown faster'
            },
            hideClass: {
                popup: 'animated fadeOutUp faster'
            }
        });
    }
}
